import Konva from 'konva';
import {without} from 'lodash';
import Hotkeys from 'react-hot-keys';
import {FormattedMessage} from 'react-intl';
import grey from '@material-ui/core/colors/grey';
import {KonvaEventObject} from 'konva/types/Node';
import BrokenImage from '@material-ui/icons/BrokenImage';
import {KonvaPointerEvent} from 'konva/types/PointerEvents';
import {CircularProgress, Tooltip} from '@material-ui/core';
import {Rect, Layer, Transformer, Stage as KonvaStage} from 'react-konva';
import React, {useMemo, useEffect, useContext, useCallback} from 'react';
import {
  Box,
  Point,
  TooltipEvent,
  BoxLabelProps,
  LineLabelProps,
  PolyLabelProps,
} from './types';

import {Polygon} from './Polygon';
import {Image, IMAGE_NAME} from './Image';
import {Label, LABEL_NAME} from './Label';
import {getScaleDir} from './utils/scaleUtils';
import {useAnnotatorRefs} from './hooks/useAnnotatorRefs';
import {AnchorPointProps, AnchorPoint} from './AnchorPoint';
import {useAnnotatorState} from './hooks/useAnnotatorState';
import {AnnotatorContext, NOT_SCALE_TO_FIT} from './Provider';
import {useImageHandler} from './hooks/useAnnotatorImageHandler';
import {useNormalizationTools} from './utils/pointsNormalizationUtils';
import {MAX_SCALE_FACTOR, MIN_SCALE_FACTOR} from '../../DetectionStep/const';
import {getAnchorPointUnderClick, getPointerPosition} from './utils/eventUtils';
import {ImageType} from '../../../../types/dataset/DatasetGetAnnotationsResponse';
import {
  STAGE_NAME,
  CURSOR_MAP,
  LOADER_SIZE,
  TRANSFORMER,
  SCALE_FACTOR,
  SELECTION_BOX,
  STAGE_HEIGHT_ADJ,
} from './const';

import './Annotator.scss';

export interface AnnotatorProps {
  src?: string;
  width?: number;
  height?: number;
  zoomEnable?: boolean;
  image?: ImageType | null | undefined;

  selectedAnnotationsIds: string[];

  polyLabels?: PolyLabelProps[];
  freePoints?: AnchorPointProps[];
  boundingBoxes?: BoxLabelProps[];

  onDelete?: (id: string) => void | void;
  onSelect?: (ids: string[]) => void | void;
  onHoverPolygon?: (poly: PolyLabelProps) => void;
  onDeleteFreePoint?: (point: Point) => void | void;
  onContextMenu?: (event: PointerEvent) => void | void;
  onPolyLabelCreated?: (label: PolyLabelProps) => void;
  onBoundingBoxCreated?: (label: BoxLabelProps) => void;
  onFreePointCreated?: (point: AnchorPointProps['point'], isRightClick: boolean) => void;
  onChangeBoundingBox?: (
    annotationId: string,
    newProp: Partial<BoxLabelProps> | Partial<PolyLabelProps>
  ) => void;
}

export function Annotator({
  image,
  width: rawWidth = 1,
  height: rawHeight = 1,
  src: rawSrc,
  boundingBoxes = [],
  selectedAnnotationsIds,
  polyLabels = [],
  freePoints = [],
  onSelect,
  onDelete,
  onContextMenu,
  onHoverPolygon = () => {},
  onFreePointCreated = () => {},
  onDeleteFreePoint = () => {},
  onChangeBoundingBox = () => {},
}: // onBoundingBoxCreated = () => {},
AnnotatorProps) {
  const {width = 1, height = 1} = useMemo(
    () => ({
      width: rawWidth > 0 ? rawWidth : 1,
      height: rawHeight > 0 ? rawHeight : 1,
    }),
    [rawWidth, rawHeight]
  );

  const {imageLayer, scale, setImageLayer, setScaleToFitFactor} = useContext(
    AnnotatorContext
  );

  const {
    tooltip,
    setTooltip,

    selector,
    setSelector,

    cursor,
    setCursor,

    isGrabbing,
    setIsGrabbing,

    isImageBusy,
    setIsImageBusy,

    isImageError,
    setIsImageError,

    isTransforming,
    setIsTransforming,

    instructionDialog,
    setInstructionDialog,
  } = useAnnotatorState();

  const {layerRef, stageRef, imageLayerRef, transformerRef} = useAnnotatorRefs();

  const clean = useCallback(() => {
    setTooltip({...tooltip, title: ''});
    setIsImageError(false);
  }, [tooltip, setTooltip, setIsImageError]);

  const {
    src,
    onError: onImageError,
    onStartLoading: onImageStartLoading,
    onFinishLoading: onImageFinishLoading,
  } = useImageHandler({
    rawSrc,
    layerRef,
    transformerRef,
    clean,
    setIsImageBusy,
    setIsImageError,
  });

  const imageWidth = useMemo(() => image?.width || 1, [image]);
  const imageHeight = useMemo(() => image?.height || 1, [image]);

  const normalizationTools = useNormalizationTools({
    width,
    height,
    imageLayer,
    imageWidth,
    imageHeight,
  });

  const handleImageInstructionsMouseMove = useCallback(
    (event: KonvaEventObject<MouseEvent>) => {
      const noAnnotations =
        (!polyLabels || polyLabels.length === 0) &&
        (!boundingBoxes || boundingBoxes.length === 0) &&
        (!freePoints || freePoints.length === 0);

      if (noAnnotations) {
        const {clientX, clientY} = event.evt;
        const containerRect = stageRef.current?.container().getBoundingClientRect();

        if (containerRect) {
          const x = clientX - containerRect.left;
          const y = clientY - containerRect.top;

          setInstructionDialog(prev => ({
            ...prev,
            x: x + 10,
            y: y + 10,
            visible: true,
            htmlContent: (
              <FormattedMessage
                tagName="span"
                id="dataPrep.segmentatioStep.firstMoveInstrucction"
              />
            ),
          }));
        }
      }
    },
    [stageRef, polyLabels, freePoints, boundingBoxes, setInstructionDialog]
  );

  const x = useMemo(() => ((imageWidth - width) / 2) * getScaleDir(width, imageWidth), [
    width,
    imageWidth,
  ]);

  const y = useMemo(
    () => ((imageHeight - height) / 2) * getScaleDir(height, imageHeight),
    [imageHeight, height]
  );

  const disableSingleLabelTransform = useCallback(() => {
    const tr = transformerRef.current;

    tr?.nodes().map(node => node.setAttrs({draggable: false}));
    tr?.nodes([]);
  }, [transformerRef]);

  const findLabelObjById = useCallback(
    (id: string | null | undefined) => {
      const stage = stageRef.current;

      return (
        stage?.find((node: Konva.Node) => {
          return node.getType() === 'Group' && node.id() === id;
        })[0] || null
      );
    },
    [stageRef]
  );

  useEffect(() => {
    if (selectedAnnotationsIds.length > 1) {
      disableSingleLabelTransform();
    } else {
      const tr = transformerRef.current;

      if (selectedAnnotationsIds.length === 1) {
        const node = findLabelObjById(selectedAnnotationsIds[0]);

        node && tr && tr.nodes([node]);
        tr?.nodes()?.map(node => node.setAttrs({draggable: true}));
      } else {
        tr?.nodes([]);
      }
    }
  }, [
    selectedAnnotationsIds,
    transformerRef,
    disableSingleLabelTransform,
    findLabelObjById,
  ]);

  const refreshCursor = useCallback(
    (target?: string) => {
      setCursor(CURSOR_MAP[isGrabbing ? 'Grabbing' : target || 'Stage']);
    },
    [isGrabbing, setCursor]
  );

  useEffect(() => refreshCursor(), [refreshCursor]);

  const onStageMouseDown = useCallback(
    (event: KonvaPointerEvent) => {
      const pointerPos = getPointerPosition(stageRef);
      const hasClickedAnImage = event.target.hasName(IMAGE_NAME);
      const hasClickedAPolygon = event.target.hasName('polygon');

      if (
        !isGrabbing &&
        (hasClickedAPolygon ||
          hasClickedAnImage ||
          (!image && event.target.hasName(STAGE_NAME)))
      ) {
        const isRightClick = event.evt.button === 2;
        const normalizedPoint = normalizationTools.normalizePoint(pointerPos);

        if (hasClickedAPolygon && isRightClick) {
          if (onSelect) {
            onSelect([event.target.id()]);
          }

          if (onContextMenu) {
            onContextMenu(event.evt);
          }
        } else {
          const underPolygonAnchor = hasClickedAPolygon
            ? getAnchorPointUnderClick(event)
            : null;

          if (hasClickedAPolygon && underPolygonAnchor) {
            onDeleteFreePoint(
              normalizationTools.normalizePoint({
                x: underPolygonAnchor.attrs.x,
                y: underPolygonAnchor.attrs.y,
              })
            );
          } else {
            const isNegativePoint = event.evt.shiftKey;

            onFreePointCreated(normalizedPoint, isNegativePoint);
          }
        }
      }
    },
    [
      image,
      stageRef,
      isGrabbing,
      normalizationTools,
      onSelect,
      onContextMenu,
      onDeleteFreePoint,
      onFreePointCreated,
    ]
  );

  const onMousemove = useCallback(
    event => {
      const stage = stageRef.current;

      if (stage && selector.visible) {
        const pointerPos = getPointerPosition(stageRef);

        setSelector(prevSelector => ({
          ...prevSelector,
          endX: pointerPos.x,
          endY: pointerPos.y,
        }));
      }
    },
    [stageRef, selector.visible, setSelector]
  );

  useEffect(() => {
    const stageContainer = stageRef.current?.container();

    if (stageContainer) {
      stageContainer.addEventListener('mousemove', onMousemove, {passive: false});
    }

    return () => {
      if (stageContainer) {
        stageContainer.removeEventListener('mousemove', onMousemove);
      }
    };
  }, [stageRef, onMousemove]);

  const onLabelClick = useCallback(
    (
      event: KonvaPointerEvent,
      label: BoxLabelProps | LineLabelProps | PolyLabelProps
    ) => {
      const target = event.currentTarget;

      target.moveToTop();
      transformerRef.current?.moveToTop();

      findLabelObjById(`${label.id}-tag`)?.moveToTop();

      if (target.hasName(LABEL_NAME)) {
        const metaPressed = event.evt.shiftKey || event.evt.ctrlKey || event.evt.metaKey;

        if (!metaPressed) {
          // Enable transform on single Label selection
          target.setAttrs({draggable: true});

          if (onSelect) {
            onSelect([label.id]);
          }
        } else if (onSelect) {
          const list = selectedAnnotationsIds.includes(label.id)
            ? without(selectedAnnotationsIds, label.id)
            : [...selectedAnnotationsIds, label.id];

          onSelect(list);
        }
      } else if (onSelect) {
        onSelect([label.id]);
      }
    },
    [selectedAnnotationsIds, transformerRef, onSelect, findLabelObjById]
  );

  useEffect(
    function avoidDefaultBrowserEventsAfterContextMenu() {
      if (stageRef.current) {
        stageRef.current.on('contentContextmenu', event => {
          event.evt.preventDefault();
        });

        return () => {
          stageRef.current?.off('contentContextmenu');
        };
      }
    },
    [stageRef.current]
  );

  const onLabelContextMenu = useCallback(
    (
      event: KonvaPointerEvent,
      label: BoxLabelProps | LineLabelProps | PolyLabelProps
    ) => {
      onLabelClick(event, label);

      if (onContextMenu) {
        onContextMenu(event.evt);
      }
    },
    [onLabelClick, onContextMenu]
  );

  const onTooltip = useCallback(
    (event: Partial<TooltipEvent>) => {
      const ref = event.refObj;

      if (event.title) {
        setTooltip({
          y: ref?.y || 0,
          title: event.title,
          x: (ref?.width || 0) / 2 + (ref?.x || 0),
        });
      } else {
        clean();
      }
    },
    [clean, setTooltip]
  );

  const onTransformStart = useCallback(
    (event: KonvaPointerEvent) => {
      setIsTransforming(true);
      onTooltip({title: ''});
    },
    [onTooltip, setIsTransforming]
  );

  const onTransformEnd = useCallback(
    (event: KonvaPointerEvent) => {
      const labelObj = event.target as any;
      const boundingBoxId = labelObj.id();

      if (boundingBoxId) {
        const currentTransformer = transformerRef.current;

        if (currentTransformer) {
          const newPos = labelObj.position();

          const newWidth = currentTransformer.width() * currentTransformer.scaleX();
          const newHeight = currentTransformer.height() * currentTransformer.scaleY();

          const actualWidth = newWidth / imageLayer.scale;
          const actualHeight = newHeight / imageLayer.scale;

          const normalizedPos = normalizationTools.normalizePoint({
            x: newPos.x,
            y: newPos.y,
          });
          const normalizedSize = {
            width: actualWidth / imageWidth,
            height: actualHeight / imageHeight,
          };

          onChangeBoundingBox(boundingBoxId, {
            x: normalizedPos.x,
            y: normalizedPos.y,
            width: normalizedSize.width,
            height: normalizedSize.height,
          });
        }
      }

      labelObj.scaleX(1);
      labelObj.scaleY(1);

      if (transformerRef.current) {
        transformerRef.current.nodes([labelObj]);
      }

      setIsTransforming(false);
    },
    [
      imageWidth,
      imageHeight,
      transformerRef,
      normalizationTools,
      imageLayer.scale,
      onChangeBoundingBox,
      setIsTransforming,
    ]
  );

  const onDeleteAnnotation = useCallback(
    (id: string) => {
      transformerRef?.current?.nodes([]);

      if (onDelete) {
        onDelete(id);
      }

      clean();
    },
    [transformerRef, onDelete, clean]
  );

  const onWheel = useCallback(
    (event: KonvaEventObject<WheelEvent>) => {
      event.evt.preventDefault();

      const newScale = Math.min(
        Math.max(
          event.evt.deltaY < 0 ? scale() * SCALE_FACTOR : scale() / SCALE_FACTOR,
          MIN_SCALE_FACTOR
        ),
        MAX_SCALE_FACTOR
      );
      const layerRef = imageLayerRef.current;
      const stage = event.target.getStage();
      const oldScale = layerRef?.scaleX() || 0;
      const pointerPosition = stage?.getPointerPosition() || {x: 0, y: 0};

      if (stage) {
        const mousePointTo = {
          x: pointerPosition.x / oldScale - (layerRef?.x() || 0) / oldScale,
          y: pointerPosition.y / oldScale - (layerRef?.y() || 0) / oldScale,
        };

        setScaleToFitFactor(NOT_SCALE_TO_FIT);
        setImageLayer({
          scale: newScale,
          x: (pointerPosition.x / newScale - mousePointTo.x) * newScale,
          y: (pointerPosition.y / newScale - mousePointTo.y) * newScale,
        });
      }
    },
    [imageLayerRef, scale, setScaleToFitFactor, setImageLayer]
  );

  const onStageMouseOver = useCallback(
    (event: KonvaEventObject<MouseEvent>) => {
      const target: string = event.target.constructor.name;

      refreshCursor(target);
    },
    [refreshCursor]
  );

  const hotKey: {[key: string]: Function} = useMemo(
    () => ({
      spaceUp: () => setIsGrabbing(false),
      spaceDown: () => setIsGrabbing(true),
    }),
    [setIsGrabbing]
  );

  const onLabelDrag = useCallback(
    (boundingBox: BoxLabelProps, props: Partial<BoxLabelProps>) => {
      const normalizedCoords = normalizationTools.normalizePoint({
        x: props.x || 0,
        y: props.y || 0,
      });

      onChangeBoundingBox(boundingBox.id, {
        ...props,
        x: normalizedCoords.x,
        y: normalizedCoords.y,
      });
    },
    [onChangeBoundingBox, normalizationTools]
  );

  const onImageDrag = useCallback(
    (event: KonvaEventObject<MouseEvent>) => {
      setImageLayer({
        ...imageLayer,
        x: event.currentTarget.attrs.x,
        y: event.currentTarget.attrs.y,
      });
    },
    [imageLayer, setImageLayer]
  );

  const boundBoxFunc = useCallback(
    (oldBoundBox: Box, newBoundBox: Box) => {
      const leftLimit = imageLayer.x + x * imageLayer.scale;
      const topLimit = imageLayer.y + y * imageLayer.scale;
      const rightLimit = leftLimit + imageWidth * imageLayer.scale;
      const bottomLimit = topLimit + imageHeight * imageLayer.scale;
      const width =
        oldBoundBox.x === newBoundBox.x
          ? // Check right limit
            newBoundBox.x + newBoundBox.width < rightLimit
            ? newBoundBox.width
            : rightLimit - oldBoundBox.x
          : // Check left limit
          leftLimit > newBoundBox.x
          ? oldBoundBox.width
          : newBoundBox.width;
      const height =
        oldBoundBox.y === newBoundBox.y
          ? // Check bottom limit
            newBoundBox.y + newBoundBox.height < bottomLimit
            ? newBoundBox.height
            : bottomLimit - oldBoundBox.y
          : // Check top limit
          topLimit > newBoundBox.y
          ? oldBoundBox.height
          : newBoundBox.height;

      return {
        width,
        height,
        rotation: newBoundBox.rotation,
        y: Math.max(topLimit, newBoundBox.y),
        x: Math.max(leftLimit, newBoundBox.x),
      } as Box;
    },
    [imageLayer, imageWidth, imageHeight, x, y]
  );

  const handleOnHoverPolygon = useCallback(
    (poly: PolyLabelProps) => {
      onHoverPolygon(poly);
    },
    [onHoverPolygon]
  );

  const handleOnMouseLeavePolygon = useCallback(
    (poly: PolyLabelProps, event: KonvaEventObject<MouseEvent>) => {
      setInstructionDialog(prev => ({...prev, visible: false}));
    },
    [setInstructionDialog]
  );

  const handleOnMouseMovePolygon = useCallback(
    (poly: PolyLabelProps, event: KonvaEventObject<MouseEvent>) => {
      const {clientX, clientY} = event.evt;
      const containerRect = stageRef.current?.container().getBoundingClientRect();

      if (containerRect) {
        const x = clientX - containerRect.left;
        const y = clientY - containerRect.top;

        setInstructionDialog({
          x: x + 10,
          y: y + 10,
          visible: true,
          htmlContent: (
            <ul>
              <li>
                <FormattedMessage
                  tagName="span"
                  id="dataPrep.segmentatioStep.predictionHoverInstrucctions.1"
                />
              </li>
              <li>
                <FormattedMessage
                  tagName="span"
                  id="dataPrep.segmentatioStep.predictionHoverInstrucctions.2"
                />
              </li>
              <li>
                <FormattedMessage
                  tagName="span"
                  id="dataPrep.segmentatioStep.predictionHoverInstrucctions.3"
                />
              </li>
            </ul>
          ),
        });
      }
    },
    [stageRef, setInstructionDialog]
  );

  return (
    <Hotkeys
      keyName="space"
      onKeyUp={(keyName: string) => hotKey[keyName + 'Up']()}
      onKeyDown={(keyName: string) => hotKey[keyName + 'Down']()}
    >
      <div className="annotator-stage_container">
        <KonvaStage
          scaleX={1}
          scaleY={1}
          width={width}
          ref={stageRef}
          style={{cursor}}
          name={STAGE_NAME}
          className="stage"
          height={height - STAGE_HEIGHT_ADJ}
          onWheel={onWheel}
          onMouseDown={onStageMouseDown}
          onMouseOver={onStageMouseOver}
        >
          <Layer
            x={imageLayer.x}
            y={imageLayer.y}
            ref={imageLayerRef}
            draggable={isGrabbing}
            scaleX={imageLayer.scale}
            scaleY={imageLayer.scale}
            onDragMove={onImageDrag}
            onMouseMove={handleImageInstructionsMouseMove}
            onMouseOut={() => setInstructionDialog(prev => ({...prev, visible: false}))}
          >
            <Image
              src={src}
              x={((imageWidth - width) / 2) * getScaleDir(width, imageWidth)}
              y={((imageHeight - height) / 2) * getScaleDir(height, imageHeight)}
              onError={onImageError}
              onStartLoading={onImageStartLoading}
              onFinishLoading={onImageFinishLoading}
            />
          </Layer>
          <Layer ref={layerRef}>
            {!isImageBusy &&
              boundingBoxes?.map((label: BoxLabelProps) => {
                const stageCoords = normalizationTools.denormalizePoint({
                  x: label.x,
                  y: label.y,
                });

                return (
                  <Label
                    key={`bounding-box-${label.id}`}
                    {...label}
                    x={stageCoords.x}
                    y={stageCoords.y}
                    onTooltip={onTooltip}
                    selected={selectedAnnotationsIds.includes(label.id)}
                    width={label.width * imageLayer.scale * imageWidth}
                    height={label.height * imageLayer.scale * imageHeight}
                    isTransforming={
                      isTransforming && selectedAnnotationsIds.includes(label.id)
                    }
                    onClick={e => onLabelClick(e, label)}
                    onDrag={props => onLabelDrag(label, props)}
                    onDelete={() => onDeleteAnnotation(label.id)}
                    onContextMenu={e => onLabelContextMenu(e, label)}
                    limit={{
                      x: 0,
                      y: 0,
                      width: imageWidth,
                      height: imageHeight,
                    }}
                  />
                );
              })}

            {!isImageBusy &&
              freePoints?.map((anchorPoint: AnchorPointProps, index) => {
                const stagePoint = normalizationTools.denormalizePoint(anchorPoint.point);

                return (
                  <AnchorPoint
                    key={`point-${index}-${anchorPoint.point.x}-${anchorPoint.point.y}`}
                    fill={anchorPoint.fill}
                    point={stagePoint}
                    onMouseOut={anchorPoint.onMouseOut}
                    onMouseOver={anchorPoint.onMouseOver}
                    onClick={() => onDeleteFreePoint(anchorPoint.point)}
                  />
                );
              })}

            {!isImageBusy &&
              polyLabels?.map((poly: PolyLabelProps) => {
                const denormalizedPoints = poly.points.map(point =>
                  normalizationTools.denormalizePoint(point)
                );

                return (
                  <Polygon
                    key={`polygon-${poly.id}`}
                    width={imageWidth * imageLayer.scale}
                    height={imageHeight * imageLayer.scale}
                    data={{...poly, points: denormalizedPoints}}
                    onHover={() => handleOnHoverPolygon(poly)}
                    onDelete={() => onDeleteAnnotation(poly.id)}
                    selected={selectedAnnotationsIds.includes(poly.id)}
                    onMouseMove={event => handleOnMouseMovePolygon(poly, event)}
                    onMouseLeave={event => handleOnMouseLeavePolygon(poly, event)}
                  />
                );
              })}

            <Transformer
              ref={transformerRef}
              {...TRANSFORMER}
              boundBoxFunc={boundBoxFunc}
              onTransformEnd={onTransformEnd}
              onTransformStart={onTransformStart}
              onMouseOver={() => setCursor('default')}
            />
            <Rect
              {...SELECTION_BOX}
              visible={selector.visible}
              x={Math.min(selector.startX, selector.endX)}
              y={Math.min(selector.startY, selector.endY)}
              width={Math.abs(selector.endX - selector.startX)}
              height={Math.abs(selector.endY - selector.startY)}
            />
          </Layer>
        </KonvaStage>
        {!isImageBusy && instructionDialog.visible ? (
          <div
            className="prediction-instructions"
            style={{
              top: instructionDialog.y,
              left: instructionDialog.x,
            }}
          >
            {instructionDialog.htmlContent}
          </div>
        ) : null}
        <Tooltip title={tooltip.title} open={!!tooltip.title}>
          <div
            className="annotator-tooltip_placeholder"
            style={{top: tooltip.y, left: tooltip.x}}
          />
        </Tooltip>
        {isImageBusy ? (
          <div
            className="annotator-progress_placeholder"
            style={{top: height / 2 - LOADER_SIZE / 2, left: width / 2 - LOADER_SIZE / 2}}
          >
            <CircularProgress size={LOADER_SIZE} />
          </div>
        ) : null}
        {isImageError ? (
          <div className="annotator-image_broken">
            <BrokenImage style={{width: 48, height: 48, color: grey[300]}} />
          </div>
        ) : null}
      </div>
    </Hotkeys>
  );
}

export default Annotator;
